import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import { workersApi, tenantApi } from '../services/api';

function Workers() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentWorkerId, setCurrentWorkerId] = useState(null);
  const [workerData, setWorkerData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Site', // Default role
    department: 'General',
  });
  const [dialogError, setDialogError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Password display state
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    password: '',
    workerName: '',
    isNewWorker: false
  });
  
  // Role confirmation state
  const [roleConfirmDialog, setRoleConfirmDialog] = useState({
    open: false,
    confirmText: '',
    pendingRole: '',
    onConfirm: null,
  });
  
  // Fetch all workers and departments
  useEffect(() => {
    fetchWorkers();
    fetchDepartments();
  }, []);

  const hasDepartments = departments.length > 0;
  
  const fetchWorkers = async () => {
    try {
      setLoading(true);
      const response = await workersApi.getAll();
      setWorkers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching workers:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDepartments = async () => {
    try {
      const response = await tenantApi.getBranding();
      const depts = response.data?.branding?.departments;
      if (Array.isArray(depts) && depts.length > 0) {
        setDepartments(depts);
      } else {
        setDepartments([]);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWorkerData({
      ...workerData,
      [name]: value,
    });
  };
  
  // Open dialog for creating a new worker
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setCurrentWorkerId(null);
    setWorkerData({
      name: '',
      email: '',
      phone: '',
      role: 'Site', // Default role
      department: hasDepartments ? departments[0] || 'General' : 'General',
    });
    setDialogError(null);
    setDialogOpen(true);
  };
  
  // Open dialog for editing a worker
  const handleOpenEditDialog = (worker) => {
    setDialogMode('edit');
    setCurrentWorkerId(worker.worker_id);
    setWorkerData({
      name: worker.name,
      email: worker.email,
      phone: worker.phone || '',
      role: worker.role, // Set role for editing
      department: hasDepartments && departments.includes(worker.department)
        ? worker.department
        : 'General',
    });
    setDialogError(null);
    setDialogOpen(true);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  
  // Handle worker save (create or update)
  const handleSaveWorker = async () => {
    // Form validation
    if (!workerData.name.trim()) {
      setDialogError('Name is required');
      return;
    }
    
    if (!workerData.email.trim()) {
      setDialogError('Email is required');
      return;
    }
    
    // Email format validation (simple check)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(workerData.email)) {
      setDialogError('Please enter a valid email address');
      return;
    }
    
    try {
      setSaving(true);
      setDialogError(null);
      const payload = hasDepartments ? workerData : { ...workerData, department: 'General' };
      
      if (dialogMode === 'create') {
        // Create new worker
        const response = await workersApi.create(payload);
        
        // Show the generated password
        if (response.data.temporary_password) {
          setPasswordDialog({
            open: true,
            password: response.data.temporary_password,
            workerName: payload.name,
            isNewWorker: true
          });
        }
      } else {
        // Update existing worker
        await workersApi.update(currentWorkerId, payload);
      }
      
      // Refresh workers list
      fetchWorkers();
      handleDialogClose();
    } catch (err) {
      console.error(`Error ${dialogMode === 'create' ? 'creating' : 'updating'} worker:`, err);
      setDialogError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle worker deletion
  const handleDeleteWorker = async (workerId) => {
    if (window.confirm('Delete this user permanently? This is irreversible. Any linked records will be detached or removed before the user is deleted.')) {
      try {
        await workersApi.delete(workerId);
        fetchWorkers();
      } catch (err) {
        console.error('Error deleting worker:', err);
        setError('Failed to delete user. Please try again.');
      }
    }
  };
  
  // Handle worker password reset
  const handleResetPassword = async (worker) => {
    if (window.confirm(`Are you sure you want to reset the password for ${worker.name}? This will generate a new random password.`)) {
      try {
        const response = await workersApi.resetPassword(worker.worker_id);
        
        // Show the new password
        if (response.data.temporary_password) {
          setPasswordDialog({
            open: true,
            password: response.data.temporary_password,
            workerName: worker.name,
            isNewWorker: false
          });
        }
      } catch (err) {
        console.error('Error resetting password:', err);
        setError('Failed to reset password. Please try again.');
      }
    }
  };

  // Handle password dialog close
  const handlePasswordDialogClose = () => {
    setPasswordDialog({
      open: false,
      password: '',
      workerName: '',
      isNewWorker: false
    });
  };

  // Copy password to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(passwordDialog.password);
  };

  // Handle role confirmation dialog close
  const handleRoleConfirmDialogClose = () => {
    setRoleConfirmDialog({
      open: false,
      confirmText: '',
      pendingRole: '',
      onConfirm: null,
    });
  };

  // Handle role change with confirmation for Office role
  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    
    if (newRole === 'Office') {
      // Show confirmation dialog for Office role
      setRoleConfirmDialog({
        open: true,
        confirmText: '',
        pendingRole: newRole,
        onConfirm: () => {
          setWorkerData({
            ...workerData,
            role: newRole
          });
          setRoleConfirmDialog({
            open: false,
            confirmText: '',
            pendingRole: '',
            onConfirm: null,
          });
        },
      });
    } else {
      // Site role doesn't need confirmation
      setWorkerData({
        ...workerData,
        role: newRole
      });
    }
  };

  // Filter workers based on search term
  const filteredWorkers = workers.filter(worker => {
    const searchLower = searchTerm.toLowerCase();
    return (
      worker.name.toLowerCase().includes(searchLower) ||
      worker.email.toLowerCase().includes(searchLower) ||
      (worker.phone || '').toLowerCase().includes(searchLower) ||
      worker.role.toLowerCase().includes(searchLower) ||
      (hasDepartments && (worker.department || '').toLowerCase().includes(searchLower))
    );
  });

  // Mobile worker card component
  const WorkerCard = ({ worker }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {worker.name}
          </Typography>
          <Chip 
            label={worker.role} 
            color={worker.role === 'Office' ? 'primary' : 'default'}
            variant={worker.role === 'Office' ? 'filled' : 'outlined'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {worker.email}
        </Typography>
        {worker.phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {worker.phone}
          </Typography>
        )}
        {hasDepartments && worker.department && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Dept: {worker.department}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          ID: {worker.worker_id}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, pb: 2, px: 2, gap: 1 }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenEditDialog(worker)}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="warning"
          startIcon={<LockIcon />}
          onClick={() => handleResetPassword(worker)}
        >
          Reset
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleDeleteWorker(worker.worker_id)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 2 }, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2
        }}>
          <Typography component="h1" variant="h5" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Users
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            fullWidth={isMobile}
          >
            Add User
          </Button>
        </Box>

        {/* Search Box */}
        <TextField
          fullWidth
          placeholder="Search users by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
          }}
        />
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Mobile view - Cards */}
            {isMobile ? (
              <Box>
                {filteredWorkers.map((worker) => (
                  <WorkerCard key={worker.worker_id} worker={worker} />
                ))}
                {filteredWorkers.length === 0 && (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {searchTerm ? 'No users found matching your search' : 'No users found'}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              /* Desktop view - Table */
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Role</TableCell>
                      {hasDepartments && <TableCell>Department</TableCell>}
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredWorkers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={hasDepartments ? 6 : 5} align="center">
                          {searchTerm ? 'No users found matching your search' : 'No users found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredWorkers.map((worker) => (
                        <TableRow key={worker.name}>
                          <TableCell>{worker.name}</TableCell>
                          <TableCell>{worker.email}</TableCell>
                          <TableCell>{worker.phone || '—'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={worker.role} 
                              color={worker.role === 'Office' ? 'primary' : 'default'}
                              variant={worker.role === 'Office' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          {hasDepartments && <TableCell>{worker.department || '—'}</TableCell>}
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEditDialog(worker)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<LockIcon />}
                              onClick={() => handleResetPassword(worker)}
                              sx={{ ml: 1 }}
                            >
                              Reset Password
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteWorker(worker.worker_id)}
                              sx={{ ml: 1 }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>
      
      {/* Create/Edit Worker Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Add User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={workerData.name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={workerData.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={workerData.phone}
              onChange={handleInputChange}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={workerData.role}
                onChange={handleRoleChange}
                label="Role"
                required
              >
                <MenuItem value="Site">Site</MenuItem>
                <MenuItem value="Site Manager">Site Manager</MenuItem>
                <MenuItem value="Office">Office</MenuItem>
              </Select>
            </FormControl>
            {hasDepartments && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={workerData.department}
                  onChange={handleInputChange}
                  label="Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleDialogClose} fullWidth={isMobile} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveWorker} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Password Dialog */}
      <Dialog 
        open={passwordDialog.open} 
        onClose={handlePasswordDialogClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {passwordDialog.isNewWorker ? 'New User Password' : 'Reset Password'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, pb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {passwordDialog.isNewWorker ? 'This is the generated password for the new user.' : 'This is the new password for the user.'}
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {passwordDialog.password}
            </Typography>
            <Button variant="contained" onClick={copyToClipboard}>
              Copy to Clipboard
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
          <Button onClick={handlePasswordDialogClose} fullWidth={isMobile}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Office Role Confirmation Dialog */}
      <Dialog 
        open={roleConfirmDialog.open} 
        onClose={handleRoleConfirmDialogClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Office Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to assign the Office role? This role has elevated permissions.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              To confirm, please type "office" in the field below:
            </Typography>
            <TextField
              fullWidth
              label="Type 'office' to confirm"
              value={roleConfirmDialog.confirmText}
              onChange={(e) => setRoleConfirmDialog({
                ...roleConfirmDialog,
                confirmText: e.target.value
              })}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleRoleConfirmDialogClose} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (roleConfirmDialog.onConfirm) {
                roleConfirmDialog.onConfirm();
              }
            }}
            variant="contained"
            color="primary"
            disabled={roleConfirmDialog.confirmText.toLowerCase() !== 'office'}
            fullWidth={isMobile}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Workers;
